import { useEffect, useState } from 'react';
import { useApi } from '../utils/useApi';
import { toDE } from '../utils/utils';

import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import Stack from 'react-bootstrap/Stack';
import Form from 'react-bootstrap/Form';

import Link from '../components/icons/Link';
import DeleteIcon from '../components/icons/DeleteIcon';
import DecisionFormModal, {type DecisionFormValues} from '../components/decisions/modals/DecisionFormModal';

import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

// type DecisionLaw = {
//   id: number;
//   law_id: number;
//   abbr: string;
//   name: string;
// };

type DecisionLawPart = {
  id: number;
  law_id: number;
  law_part_id: number;
  abbr: string;
  name: string;
  paragraphs: string | null;
};

type LatestLawOption = {
  id: number;
  law_id: number;
  abbr: string;
  name: string;
};

type LatestLawPartOption = {
  law_id: number;
  law_part_id: number;
  paragraphs: string | null;
};

type DecisionFile = {
  id: number;
  filename: string;
  original_filename?: string;
  url?: string;

  decision_id: number;
  file_name: string;
  description: string | null;
  upload_date: string | null;
  uploaded_by: number | null;
  active: boolean;
};

type DecisionEntry = {
  id: number;
  file_number: string | null;
  name: string | null;
  authority: string | null;
  decision_date: string | null;
  subject: string | null;
  // laws: DecisionLaw[];
  laws: DecisionLawPart[];
  law_parts: DecisionLawPart[];
  files: DecisionFile[];
};

export default function DecisionRegister() {
  const { apiFetch } = useApi();

  const [decisions, setDecisions] = useState<DecisionEntry[]>([]);
  const [selectedDecision, setSelectedDecision] = useState<DecisionEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [availableLaws, setAvailableLaws] = useState<LatestLawOption[]>([]);
  const [availableLawParts, setAvailableLawParts] = useState<LatestLawPartOption[]>([]);
  const [allClientLawParts, setAllClientLawParts] = useState<DecisionLawPart[]>([]);
  const [showLinkingLawModal, setShowLinkingLawModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [lawLinkError, setLawLinkError] = useState<string | null>(null);

  const [showDecisionFormModal, setShowDecisionFormModal] = useState(false);
  const [editingDecision, setEditingDecision] = useState<DecisionEntry | null>(null);
  const [savingDecision, setSavingDecision] = useState(false);
  const [decisionFormError, setDecisionFormError] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState<keyof DecisionEntry>('decision_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [lawFilter, setLawFilter] = useState('');
  const [lawSortField, setLawSortField] = useState<keyof LatestLawOption>('name');
  const [lawSortDirection, setLawSortDirection] = useState<'asc' | 'desc'>('asc');

  const {decision_id} = useParams<{ decision_id?: string }>();
  const navigate = useNavigate();
  
  const handleRowClick = (decision: DecisionEntry) => {
    navigate(`/bescheide/${decision.id}`);
    console.log('Editing Decision:', decision);
  };

  const openCreateDecisionModal = () => {
    setEditingDecision(null);
    setDecisionFormError(null);
    setShowDecisionFormModal(true);
  };

  const openEditDecisionModal = (decision: DecisionEntry) => {
    setEditingDecision(decision);
    setDecisionFormError(null);
    setShowDecisionFormModal(true);
  };

  const saveDecision = async (values: DecisionFormValues) => {
    setSavingDecision(true);
    setDecisionFormError(null);

    try {
      if (editingDecision) {
        const updatedDecision = await apiFetch(
          `/api/decisions/${editingDecision.id}`,
          'PATCH',
          values
        );

        setDecisions((current) =>
          current.map((decision) =>
            decision.id === updatedDecision.id ? updatedDecision : decision
          )
        );

        setSelectedDecision(updatedDecision);
      } else {
        const createdDecision = await apiFetch(
          '/api/decisions',
          'POST',
          values
        );

        setDecisions((current) => [createdDecision, ...current]);
        setSelectedDecision(createdDecision);
      }

      setShowDecisionFormModal(false);
      setEditingDecision(null);
    } catch (err) {
      console.error('Bescheid konnte nicht gespeichert werden', err);
      setDecisionFormError('Bescheid konnte nicht gespeichert werden.');
    } finally {
      setSavingDecision(false);
    }
  };

  const linkLawPartToDecision = async (lawPartId: number) => {
    if (!selectedDecision || !lawPartId) {
      setLawLinkError('Bitte wählen Sie eine Vorschrift aus.');
      return;
    }

    setLawLinkError(null);

    try {
      const linkedLawPart = await apiFetch(
        `/api/decisions/${selectedDecision.id}/law_parts`,
        'POST',
        {
          law_part_id: lawPartId,
        }
      );

      const updatedDecision = {
        ...selectedDecision,
        laws: [...selectedDecision.laws, linkedLawPart],
        law_parts: [...selectedDecision.law_parts, linkedLawPart],
      };

      console.log('Updated Decision:', updatedDecision);
      setSelectedDecision(updatedDecision);

      setDecisions((current) =>
        current.map((decision) =>
          decision.id === updatedDecision.id ? updatedDecision : decision
        )
      );

    } catch (err) {
      console.error('Vorschrift konnte nicht verknüpft werden', err);
      setLawLinkError('Vorschrift konnte nicht verknüpft werden.');
    }
  };

  const removeLawLink = async (lawPartId: number) => {
    if (!selectedDecision || !lawPartId) {
      return;
    }

    try {
      await apiFetch(
        `/api/decisions/${selectedDecision.id}/law_parts/${lawPartId}`,
        'DELETE'
      );

      const updatedDecision = {
        ...selectedDecision,
        law_parts: selectedDecision.law_parts.filter((law_part) => law_part.law_part_id !== lawPartId),
      };

      console.log('Updated Decision after law unlink:', updatedDecision);

      setSelectedDecision(updatedDecision);

      setDecisions((current) =>
        current.map((decision) =>
          decision.id === updatedDecision.id ? updatedDecision : decision
        )
      );

    } catch (err) {
      console.error('Vorschrift konnte nicht entfernt werden', err);
    }
  };

  const uploadDecisionFile = async () => {
    if (!selectedDecision || !uploadFile) {
      setUploadError('Bitte wählen Sie eine Datei aus.');
      return;
    }

    setUploading(true);
    setUploadError(null);

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('description', uploadDescription);

    try {
      const uploaded = await apiFetch(
        `/api/decisions/${selectedDecision.id}/files`,
        'POST',
        formData
      );

      const updatedDecision = {
        ...selectedDecision,
        files: [...selectedDecision.files, uploaded],
      };

      setSelectedDecision(updatedDecision);

      setDecisions((current) =>
        current.map((decision) =>
          decision.id === updatedDecision.id ? updatedDecision : decision
        )
      );

      setUploadFile(null);
      setUploadDescription('');
    } catch (err) {
      console.error('Datei konnte nicht hochgeladen werden', err);
      setUploadError('Datei konnte nicht hochgeladen werden.');
    } finally {
      setUploading(false);
    }
  };

  const openFile = (decisionId: number, fileName: string) => {
    const encodedFileName = encodeURIComponent(fileName);
    const fileUrl = `/api/decisions/${decisionId}/files/${encodedFileName}`;
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  };

  const removeDecisionFile = (fileId: number) => async () => {
    if (!selectedDecision) {
      return;
    }

    try {
      await apiFetch(
        `/api/decisions/${selectedDecision.id}/files/${fileId}`,
        'DELETE'
      );

      const updatedDecision = {
        ...selectedDecision,
        files: selectedDecision.files.filter((file) => file.id !== fileId),
      };

      setSelectedDecision(updatedDecision);

      setDecisions((current) =>
        current.map((decision) =>
          decision.id === updatedDecision.id ? updatedDecision : decision
        )
      );

    } catch (err) {
      console.error('Datei konnte nicht entfernt werden', err);
    }
  };

  const toggleSort = (field: keyof DecisionEntry) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  const sortIcon = (field: keyof DecisionEntry) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const filteredAndSortedDecisions = decisions
    .filter((decision) => {
      const search = filter.toLowerCase();

      return [
        decision.file_number,
        decision.name,
        decision.authority,
        decision.decision_date,
        decision.subject,
        ...decision.laws.map((law) => law.abbr),
        ...decision.laws.map((law) => law.name),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));
    })
    .sort((a, b) => {
      const aValue = a[sortField] ?? '';
      const bValue = b[sortField] ?? '';

      const result = String(aValue).localeCompare(String(bValue), 'de', {
        numeric: true,
        sensitivity: 'base',
      });

      return sortDirection === 'asc' ? result : -result;
    });

  const closeDecisionModal = () => {
    setSelectedDecision(null);
    navigate('/bescheide');
  };


  const toggleLawSort = (field: keyof LatestLawOption) => {
    if (lawSortField === field) {
      setLawSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setLawSortField(field);
    setLawSortDirection('asc');
  };

  const lawSortIcon = (field: keyof LatestLawOption) => {
    if (lawSortField !== field) return '↕';
    return lawSortDirection === 'asc' ? '↑' : '↓';
  };

  const filteredAndSortedAvailableLaws = availableLaws
    .filter(
      (law) =>
        !selectedDecision?.laws.some(
          (linkedLaw) => linkedLaw.law_id === law.law_id
        )
    )
    .filter((law) => {
      const search = lawFilter.toLowerCase();

      return [law.abbr, law.name, law.law_id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search));
    })
    .sort((a, b) => {
      const aValue = a[lawSortField] ?? '';
      const bValue = b[lawSortField] ?? '';

      const result = String(aValue).localeCompare(String(bValue), 'de', {
        numeric: true,
        sensitivity: 'base',
      });

      return lawSortDirection === 'asc' ? result : -result;
    });

  const lawPartsByLawId = availableLawParts.reduce<Record<number, LatestLawPartOption[]>>(
    (groups, lawPart) => {
      groups[lawPart.law_id] ??= [];
      groups[lawPart.law_id].push(lawPart);
      return groups;
    },
    {}
  );

  // const closeDecisionFormModal = () => {
  //   setShowDecisionFormModal(false);
  //   setEditingDecision(null);
  // }

  useEffect(() => {
    apiFetch('/api/decisions')
      .then((data) => setDecisions(data))
      .catch((err) => {
        console.error('Bescheide konnten nicht geladen werden', err);
        setError('Bescheide konnten nicht geladen werden.');
      })
      .finally(() => setLoading(false));

    apiFetch('/api/client-latest-laws')
      .then((data) => setAvailableLaws(data))
      .catch((err) => {
        console.error('Vorschriften konnten nicht geladen werden', err);
        // setError('Vorschriften konnten nicht geladen werden.');
      });

    apiFetch('/api/client-latest-law-parts')
      .then((data) => {
        setAvailableLawParts(data);
        setAllClientLawParts(data);
      })
      .catch((err) => {
        console.error('Vorschriften Teile konnten nicht geladen werden', err);
        // setError('Vorschriften Teile konnten nicht geladen werden.');
      });
  }, []);

  useEffect(() => {
    if (decision_id) {
      const decision = decisions.find((d) => d.id === parseInt(decision_id));
      if (decision) {
        setSelectedDecision(decision);
        // setShowModal(true);
      }
    }
  }, [decision_id, decisions]);
  
{/* 
//#region HTML
*/}
  return (
    <>
      {loading && <Spinner animation="border" />}
      {error && <Alert variant="danger">{error}</Alert>}
      {!loading && !error && (
        <div className="container mt-4">
          <Stack direction="horizontal" gap={3} className="mb-3">
              <h2 className="mb-0">Bescheidregister</h2>
              <Button variant="outline-primary" size="sm" onClick={openCreateDecisionModal}>
                + Neuer Bescheid
              </Button>
          </Stack>
          <Stack direction="horizontal" gap={0}>
              <Form.Control
                type="search"
                placeholder="Filtern nach Aktenzeichen, Inhalt, Vorschriften..."
                className="p-2"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
          </Stack>
          <div className="text-muted small my-2">
            {filteredAndSortedDecisions.length} von {decisions.length} Bescheiden
          </div>
          <div className='table-scroll-wrapper'>
            <Table hover responsive>
              <thead>
                <tr>
                  <th role="button" onClick={() => toggleSort('authority')}>
                    Behörde&nbsp;{sortIcon('authority')}
                  </th>
                  <th role="button" onClick={() => toggleSort('decision_date')}>
                    Ausstellungsdatum&nbsp;{sortIcon('decision_date')}
                  </th>
                  <th role="button" onClick={() => toggleSort('file_number')}>
                    Aktenzeichen&nbsp;{sortIcon('file_number')}
                  </th>
                  <th role="button" onClick={() => toggleSort('name')}>
                    Thematik&nbsp;{sortIcon('name')}
                  </th>
                  <th role="button" onClick={() => toggleSort('subject')}>
                    Inhalt&nbsp;{sortIcon('subject')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedDecisions.map((decision) => (
                  <tr
                      key={decision.id}
                      role="button"
                      // onClick={() => setSelectedDecision(decision)}
                      onClick={() => handleRowClick(decision)}
                 >
                      <td>{decision.authority}</td>
                      <td>{decision.decision_date ? toDE(decision.decision_date) : ''}</td>
                      <td>{decision.file_number}</td>
                      <td>{decision.name}</td>
                      <td>{decision.subject}</td>
                  </tr>
                  ))}
              </tbody>
            </Table>
          </div>
{/* 
//#region selectedDecisionModal
*/}
          <Modal
              show={selectedDecision !== null}
              onHide={() => closeDecisionModal()}
              size="lg"
          >
              <Modal.Header closeButton>
                <Modal.Title>
                  <Stack direction="horizontal" gap={3} className="mb-3">
                    {selectedDecision?.name}
                    <Button variant="outline-primary" size="sm" onClick={() => selectedDecision && openEditDecisionModal(selectedDecision)}>Bearbeiten</Button>
                  </Stack>
                </Modal.Title>
              </Modal.Header>

              <Modal.Body>
              {selectedDecision && (
                  <>
                  <dl>
                      <dt>Behörde</dt>
                      <dd>{selectedDecision.authority}</dd>

                      <dt>Ausstellungsdatum</dt>
                      <dd>
                      {selectedDecision.decision_date
                          ? toDE(selectedDecision.decision_date)
                          : ''}
                      </dd>

                      <dt>Aktenzeichen</dt>
                      <dd>{selectedDecision.file_number}</dd>

                      <dt>Thematik</dt>
                      <dd>{selectedDecision.name}</dd>

                      <dt>Inhalt</dt>
                      <dd>{selectedDecision.subject}</dd>
                  </dl>

                  <hr />
                  
                  <h5>Referenzierte Vorschriften</h5>

                  {selectedDecision.law_parts.length > 0 ? (
                    <Table bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Abkürzung</th>
                          <th>Bezeichnung</th>
                          <th>Paragrafen</th>
                          <th></th>
                       </tr>
                      </thead>
                      <tbody>
                        {selectedDecision.law_parts.map((law_part) => (
                          <tr key={law_part.law_part_id}>
                            <td>{allClientLawParts.find((lp) => lp.law_part_id === law_part.law_part_id)?.abbr || law_part.abbr}</td>
                            <td role="button" onClick={() => navigate(`/documents/${law_part.law_part_id}`)} style={{cursor: 'pointer', textDecoration: 'underline'}}>{allClientLawParts.find((lp) => lp.law_part_id === law_part.law_part_id)?.name || law_part.name}</td>
                            <td>{law_part.paragraphs}</td>
                            {/* <td>{law_part.abbr}</td>
                            <td>{law_part.name}</td>
                            <td>{law_part.paragraphs}</td> */}
                            <td onClick={() => removeLawLink(law_part.law_part_id)}><DeleteIcon /></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p>Keine Vorschriften verknüpft.</p>
                  )}
                  <Button variant="outline-primary" size="sm" onClick={() => setShowLinkingLawModal(true)}>
                    + Neue Vorschrift verknüpfen
                  </Button>

                  <hr />

                  <h5>Dateien</h5>

                  {selectedDecision.files.length > 0 ? (
                    <Table bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Dateiname</th>
                          <th>Beschreibung</th>
                          <th>Upload</th>
                          <th></th>
                       </tr>
                      </thead>
                      <tbody>
                        {selectedDecision.files.map((file) => (
                          <tr key={file.id}>
                            <td onClick={() => openFile(selectedDecision.id, file.file_name)}>
                              {file.file_name}
                            </td>
                            <td>{file.description}</td>
                            <td>{file.upload_date ? toDE(file.upload_date) : ''}</td>
                            <td onClick={removeDecisionFile(file.id)}><DeleteIcon /></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  ) : (
                    <p>Keine Dateien vorhanden.</p>
                  )}
                  <Button variant="outline-primary" size="sm" onClick={() => setShowUploadModal(true)}>
                    + Neue Datei hochladen
                  </Button>
                  </>
              )}
              </Modal.Body>

              <Modal.Footer>
                <Button variant="secondary" onClick={() => closeDecisionModal()}>
                    Schließen
                </Button>
              </Modal.Footer>
          </Modal>
{/* 
//#region LinkingLawModal
*/}
          <Modal
            show={showLinkingLawModal} 
            onHide={() => setShowLinkingLawModal(false)} 
            size="lg"
            backdrop
            className="stacked-modal"
            backdropClassName="stacked-modal-backdrop"
            >
            <Modal.Header closeButton>
              <Modal.Title>Vorschrift verknüpfen</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {lawLinkError && <Alert variant="danger">{lawLinkError}</Alert>}

              <Form.Control
                type="search"
                placeholder="Filtern nach Abkürzung oder Bezeichnung..."
                className="mb-3"
                value={lawFilter}
                onChange={(e) => setLawFilter(e.target.value)}
              />

              <div className="text-muted small mb-2">
                {filteredAndSortedAvailableLaws.length} verfügbare Vorschriften
              </div>

              <Table bordered hover size="sm">
                <thead>
                  <tr>
                    <th role="button" onClick={() => toggleLawSort('abbr')}>
                      Abkürzung&nbsp;{lawSortIcon('abbr')}
                    </th>
                    <th role="button" onClick={() => toggleLawSort('name')}>
                      Bezeichnung&nbsp;{lawSortIcon('name')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedAvailableLaws.map((law) => {
                    const lawParts = lawPartsByLawId[law.law_id] ?? [];
                    return (
                      <>
                        <tr key={`law-${law.law_id}`} id={`law-${law.law_id}`}>
                          <td>{law.abbr}</td>
                          <td>{law.name}</td>
                        </tr>
                        {lawParts.map((lawPart) => (
                          <tr key={`part-${law.law_id}-${lawPart.law_part_id}`} className="table-secondary" id={`part-${law.law_id}-${lawPart.law_part_id}`}>
                            <td colSpan={2} onClick={() => linkLawPartToDecision(lawPart.law_part_id)}>
                              <Link /> {lawPart.paragraphs}</td>
                          </tr>
                        ))}
                      </>
                    );
                  })}
                </tbody>
              </Table>
              {filteredAndSortedAvailableLaws.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-muted text-center">
                    Keine passenden Vorschriften gefunden.
                  </td>
                </tr>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowLinkingLawModal(false)}>
                  Schließen
              </Button>
            </Modal.Footer>
          </Modal>
{/* 
//#region UploadModal  
*/}        
          <Modal 
            size="lg" 
            show={showUploadModal} 
            onHide={() => setShowUploadModal(false)}
            backdrop
            className="stacked-modal"
            backdropClassName="stacked-modal-backdrop"
           >
            <Modal.Header closeButton>
              <Modal.Title>Datei hochladen</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <h5>Datei hochladen</h5>

              {uploadError && <Alert variant="danger">{uploadError}</Alert>}

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Datei</Form.Label>
                  <Form.Control
                    type="file"
                    onChange={(event) => {
                      const input = event.target as HTMLInputElement;
                      setUploadFile(input.files?.[0] ?? null);
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Beschreibung</Form.Label>
                  <Form.Control
                    type="text"
                    value={uploadDescription}
                    onChange={(event) => setUploadDescription(event.target.value)}
                    placeholder="Optionale Beschreibung"
                  />
                </Form.Group>

                <Button
                  variant="primary"
                  onClick={uploadDecisionFile}
                  disabled={uploading || !uploadFile}
                >
                  {uploading ? 'Wird hochgeladen...' : 'Datei hochladen'}
                </Button>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowUploadModal(false)}>
                  Schließen
              </Button>
            </Modal.Footer>
          </Modal>
{/* 
//#region DecisionFormModal
*/}
          <DecisionFormModal
            show={showDecisionFormModal}
            title={editingDecision ? 'Bescheid bearbeiten' : 'Neuen Bescheid anlegen'}
            initialValues={
              editingDecision
                ? {
                    file_number: editingDecision.file_number ?? '',
                    name: editingDecision.name ?? '',
                    authority: editingDecision.authority ?? '',
                    decision_date: editingDecision.decision_date ?? '',
                    subject: editingDecision.subject ?? '',
                  }
                : undefined
            }
            saving={savingDecision}
            error={decisionFormError}
            onHide={() => setShowDecisionFormModal(false)}
            onSubmit={saveDecision}
          />
        </div>
      )}
    </>
  );
}